import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { APP_VERSION, getRuntimeDocument, getRuntimeWindow } from '@imagise/shared';
import {
  buildErrorReportContext,
  DEFAULT_DEDUP_OPTIONS,
  DEFAULT_REDACTION_RULES,
  DedupStorage,
  ErrorReportDeduplicator,
  ErrorReportEnvironment,
  ErrorReportContext,
  ErrorReportOperation,
  operationAppError,
  readReportFile,
  ErrorReportSeverity,
  ErrorReportStore,
  fingerprintErrorContext,
  redactErrorContext,
} from '@imagise/entities';
import {
  ERROR_REPORT_PROMPTER,
  ERROR_REPORT_FEEDBACK_AUTH,
  ERROR_REPORTING_CONFIG,
} from './error-reporting.config';

/**
 * Orchestrates a single capture: collect environment → build context → redact →
 * deduplicate → store → surface (navigate to the recovery screen for fatal
 * crashes, prompt for non-fatal ones). Shared by the global {@link ErrorHandler}
 * and the browser error listeners.
 */
@Injectable({ providedIn: 'root' })
export class ErrorReportingService {
  readonly #feedbackAuth = inject(ERROR_REPORT_FEEDBACK_AUTH);
  readonly #config = inject(ERROR_REPORTING_CONFIG);
  readonly #router = inject(Router);
  readonly #platformId = inject(PLATFORM_ID);
  readonly #store = inject(ErrorReportStore);
  readonly #appVersion = inject(APP_VERSION, { optional: true });
  readonly #prompter = inject(ERROR_REPORT_PROMPTER, { optional: true });

  readonly #deduplicator = new ErrorReportDeduplicator(this.#resolveStorage(), {
    ...DEFAULT_DEDUP_OPTIONS,
    ...this.#config.dedupOptions,
  });

  #reporting = false;
  readonly #queuedOperations = new Map<string, ErrorReportOperation>();

  trackOperationResult(response: unknown, operation: ErrorReportOperation): void {
    if (!isPlatformBrowser(this.#platformId) || !operation.files?.length || !response || typeof response !== 'object') return;
    const body = response as Record<string, unknown>;
    const id = body['operationId'] ?? body['OperationId'];
    if (typeof id !== 'string' || id.length > 128 || body['state'] !== undefined || body['State'] !== undefined) return;
    this.#queuedOperations.set(id, { ...operation, parameters: { ...operation.parameters }, files: [...operation.files] });
    // ponytail: retain the last ten queued inputs in this tab; durable cross-session evidence belongs on the server.
    if (this.#queuedOperations.size > 10) this.#queuedOperations.delete(this.#queuedOperations.keys().next().value!);
  }


  openFeedback(): boolean {
    if (!isPlatformBrowser(this.#platformId) || !this.#feedbackAuth?.isAuthenticated()) return false;
    const context: ErrorReportContext = {
      ...this.#collectEnvironment('non-fatal'), kind: 'feedback', type: 'Feedback', message: '', errors: [],
    };
    this.#prompter?.prompt(context);
    return true;
  }

  reportOperationError(error: unknown, operation: ErrorReportOperation): void {
    if (!isPlatformBrowser(this.#platformId)) return;
    const record = error && typeof error === 'object' ? error as Record<string, unknown> : {};
    const status = typeof record['status'] === 'number' ? record['status'] : operation.status;
    if (record['name'] === 'AbortError' || status === 401 || status === 403 || status === 429 ||
        /\/(?:ReportError|ReportFeedback|ErrorReports)(?:[/?#]|$)/i.test(operation.endpoint ?? '')) return;
    const snapshot = { ...operation, status, parameters: { ...operation.parameters }, files: operation.files?.map(file => ({ ...file })) };
    const body = record['error'];
    if (body instanceof Blob && /json/i.test(body.type) && body.size <= 1024 * 1024) {
      void readReportFile(body).then(bytes => {
        const text = new TextDecoder().decode(bytes);
        let parsed: unknown;
        try { parsed = JSON.parse(text); } catch { parsed = undefined; }
        this.#captureOperation({ ...record, message: record['message'], stack: record['stack'], error: parsed }, snapshot);
      }).catch(() => this.#captureOperation(error, snapshot));
    } else {
      this.#captureOperation(error, snapshot);
    }
  }

  #captureOperation(error: unknown, operation: ErrorReportOperation): void {
    if (this.#reporting) return;
    this.#reporting = true;
    try {
      const record = error && typeof error === 'object' ? error as Record<string, unknown> : {};
      const body = record['error'] && typeof record['error'] === 'object' ? record['error'] as Record<string, unknown> : {};
      const operationId = body['operationId'] ?? body['OperationId'];
      const original = typeof operationId === 'string' ? this.#queuedOperations.get(operationId) : undefined;
      if (original) operation = { ...original, status: operation.status, operationId: operationId as string };
      const requestId = body['traceId'] ?? body['TraceIdentifier'];
      if (typeof requestId === 'string') operation = { ...operation, requestId };
      const appError = operationAppError(error);
      const context = redactErrorContext({
        ...this.#collectEnvironment('non-fatal'), kind: 'error', operation,
        type: appError.Type, message: appError.ExceptionMessage, errors: [appError],
      });
      const fingerprint = fingerprintErrorContext(context) + operation.operation + (operation.endpoint ?? '');
      if (this.#deduplicator.shouldSuppress(fingerprint)) return;
      this.#store.capture(context);
      this.#prompter?.prompt(context);
    } catch { /* Reporting must never replace the operation's own error handling. */ }
    finally { this.#reporting = false; }
  }

  #navigating = false;

  /**
   * Captures an error and, unless it is a duplicate/rate-limited crash loop,
   * stores the redacted incident and surfaces it to the user. Never throws —
   * failures inside the reporter are swallowed so it can't break the host app.
   */
  report(error: unknown, severity: ErrorReportSeverity): void {
    if (!isPlatformBrowser(this.#platformId) || this.#reporting) return;
    this.#reporting = true;
    try {
      const context = redactErrorContext(
        buildErrorReportContext(error, this.#collectEnvironment(severity)),
        this.#resolveRedactionRules()
      );

      const suppressed = this.#deduplicator.shouldSuppress(fingerprintErrorContext(context));
      if (!suppressed) this.#store.capture(context);

      if (severity === 'fatal') {
        this.#prompter?.close?.();
        const route = this.#config.errorRoutePath ?? '/error';
        // A duplicate must not open another form, but recovery must remain reachable.
        if (this.#safeRouterUrl()?.split(/[?#]/, 1)[0] !== route && !this.#navigating) {
          this.#navigating = true;
          void Promise.resolve(this.#router.navigateByUrl(route))
            .catch(() => undefined)
            .finally(() => { this.#navigating = false; });
        }
      } else if (!suppressed) {
        this.#prompter?.prompt(context);
      }
    } catch {
      // The error reporter must never throw inside the global handler.
    } finally {
      this.#reporting = false;
    }
  }

  #collectEnvironment(severity: ErrorReportSeverity): ErrorReportEnvironment {
    const isBrowser = isPlatformBrowser(this.#platformId);
    const win = isBrowser ? getRuntimeWindow() : null;
    const doc = isBrowser ? getRuntimeDocument() : null;

    return {
      appName: this.#config.appName,
      appVersion: this.#config.appVersion ?? this.#appVersion ?? null,
      isProduction: this.#config.isProduction,
      severity,
      pageUrl: doc?.location?.href ?? '',
      routePath: this.#safeRouterUrl(),
      userAgent: win?.navigator?.userAgent ?? '',
      online: win?.navigator?.onLine ?? true,
      timestamp: new Date().toISOString(),
    };
  }

  #resolveRedactionRules() {
    return this.#config.redactionRules?.length
      ? [...DEFAULT_REDACTION_RULES, ...this.#config.redactionRules]
      : undefined;
  }

  #safeRouterUrl(): string | undefined {
    try {
      return this.#router.url;
    } catch {
      return undefined;
    }
  }

  #resolveStorage(): DedupStorage | null {
    if (!isPlatformBrowser(this.#platformId)) {
      return null;
    }

    try {
      return sessionStorage;
    } catch {
      return null;
    }
  }
}
